import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, LogOut, ImageIcon, Clock, CheckCircle2, AlertCircle, ArrowLeft, Loader2, Users, Globe, MapPin, BarChart2, Link, FileText, MousePointerClick, ChevronDown, ChevronUp } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const API_BASE = 'https://crm-backend-6jqe.onrender.com';
const ADMIN_PASS = 'Vyjednameweb5029';
const MAX_FILE_MB = 5;

const apiFetch = (path, options = {}) =>
  fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'x-banner-key': ADMIN_PASS, 'Content-Type': 'application/json', ...options.headers }
  });

const AdminPanel = () => {
  const [loggedIn, setLoggedIn] = useState(() => sessionStorage.getItem('admin_auth') === '1');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [images, setImages] = useState([]);
  const [intervalSec, setIntervalSec] = useState(5);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [loadError, setLoadError] = useState(false);
  const fileRef = useRef(null);
  const pdfRefs = useRef({});
  const [expandedClick, setExpandedClick] = useState(null); // banner id with open click settings
  const [clickSaving, setClickSaving] = useState({}); // { [id]: bool }

  const [activeTab, setActiveTab] = useState('bannery');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const showSaved = () => { setSavedMsg('Uloženo'); setTimeout(() => setSavedMsg(''), 2500); };

  const loadBanners = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetch(`${API_BASE}/api/banners`);
      const data = await res.json();
      if (Array.isArray(data.images)) setImages(data.images);
      if (data.intervalSec) setIntervalSec(data.intervalSec);
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loggedIn) {
      loadBanners();
      loadStats();
    }
  }, [loggedIn]);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await apiFetch('/api/visits/stats');
      const data = await res.json();
      setStats(data);
    } catch (_) {}
    setStatsLoading(false);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === 'admin' && password === ADMIN_PASS) {
      sessionStorage.setItem('admin_auth', '1');
      setLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Nesprávné přihlašovací údaje.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    setLoggedIn(false);
    setUsername('');
    setPassword('');
  };

  const handleFiles = async (e) => {
    setUploadError('');
    const files = Array.from(e.target.files);
    e.target.value = '';
    setUploading(true);
    for (const file of files) {
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        setUploadError(`"${file.name}" přesahuje ${MAX_FILE_MB} MB.`);
        continue;
      }
      const formData = new FormData();
      formData.append('image', file);
      try {
        const res = await fetch(`${API_BASE}/api/banners/upload`, {
          method: 'POST',
          headers: { 'x-banner-key': ADMIN_PASS },
          body: formData
        });
        if (!res.ok) throw new Error(await res.text());
      } catch (err) {
        setUploadError(`Chyba při nahrávání "${file.name}": ${err.message}`);
      }
    }
    setUploading(false);
    showSaved();
    loadBanners();
  };

  const handleDelete = async (id) => {
    try {
      const res = await apiFetch(`/api/banners/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      showSaved();
      loadBanners();
    } catch {
      setUploadError('Smazání selhalo.');
    }
  };

  const handleInterval = async (val) => {
    const num = Math.max(1, Math.min(300, parseInt(val, 10) || 5));
    setIntervalSec(num);
    try {
      await apiFetch('/api/banners/interval', { method: 'PUT', body: JSON.stringify({ intervalSec: num }) });
      showSaved();
    } catch {}
  };

  const moveImage = async (index, dir) => {
    const updated = [...images];
    const target = index + dir;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setImages(updated);
    try {
      await apiFetch('/api/banners/order', { method: 'PUT', body: JSON.stringify({ order: updated.map(img => img.id) }) });
      showSaved();
    } catch {}
  };

  const handleSaveClick = async (img) => {
    setClickSaving(s => ({ ...s, [img.id]: true }));
    try {
      await apiFetch(`/api/banners/${encodeURIComponent(img.id)}/click`, {
        method: 'PUT',
        body: JSON.stringify({ clickType: img.clickType || 'none', clickTarget: img.clickTarget || '' })
      });
      showSaved();
    } catch {
      setUploadError('Uložení akce selhalo.');
    }
    setClickSaving(s => ({ ...s, [img.id]: false }));
  };

  const handlePdfUpload = async (imgId, file) => {
    if (!file) return;
    setClickSaving(s => ({ ...s, [imgId]: true }));
    const formData = new FormData();
    formData.append('pdf', file);
    try {
      const res = await fetch(`${API_BASE}/api/banners/${encodeURIComponent(imgId)}/pdf`, {
        method: 'POST',
        headers: { 'x-banner-key': ADMIN_PASS },
        body: formData
      });
      if (!res.ok) throw new Error(await res.text());
      showSaved();
      loadBanners();
    } catch (err) {
      setUploadError(`Chyba při nahrávání PDF: ${err.message}`);
    }
    setClickSaving(s => ({ ...s, [imgId]: false }));
  };

  const updateImageField = (id, field, value) => {
    setImages(imgs => imgs.map(img => img.id === id ? { ...img, [field]: value } : img));
  };

  /* ---- LOGIN SCREEN ---- */
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/20">
              <ImageIcon size={26} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin panel</h1>
            <p className="text-sm text-gray-500 mt-1">Správa bannerů – vyjedname.cz</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Uživatelské jméno</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Heslo</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                autoComplete="current-password"
                required
              />
            </div>
            {loginError && (
              <p className="text-sm text-red-600 flex items-center gap-1.5 bg-red-50 rounded-lg px-3 py-2">
                <AlertCircle size={14} className="flex-shrink-0" /> {loginError}
              </p>
            )}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-3 rounded-xl font-semibold text-sm transition-all shadow-lg shadow-red-500/20 mt-2"
            >
              Přihlásit se
            </button>
          </form>
          <div className="mt-6 text-center">
            <a href="/" className="text-xs text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1 transition-colors">
              <ArrowLeft size={12} /> Zpět na web
            </a>
          </div>
        </div>
      </div>
    );
  }

  /* ---- ADMIN SCREEN ---- */
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-sm">
            <ImageIcon size={17} className="text-white" />
          </div>
          <div>
            <h1 className="font-bold text-gray-900 text-sm">Admin panel</h1>
            <p className="text-xs text-gray-400">vyjedname.cz</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <a href="/" className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1 transition-colors">
            <ArrowLeft size={12} /> Web
          </a>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors">
            <LogOut size={15} /> Odhlásit
          </button>
        </div>
      </header>

      {/* Tab navigation */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-1 max-w-3xl mx-auto">
          {[{ id: 'bannery', label: 'Bannery', icon: ImageIcon }, { id: 'analytika', label: 'Návštěvnost', icon: BarChart2 }].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); if (tab.id === 'analytika') loadStats(); }}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <tab.icon size={14} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'bannery' && (
      <div className="max-w-3xl mx-auto p-6 space-y-5">

        {/* Interval */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-red-600" />
            <h2 className="font-semibold text-gray-900 text-sm">Interval přepínání</h2>
          </div>
          <p className="text-xs text-gray-400 mb-3">Jak dlouho (v sekundách) se zobrazí každý banner, než se přepne na další.</p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="1"
              max="300"
              value={intervalSec}
              onChange={e => handleInterval(e.target.value)}
              className="w-24 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <span className="text-sm text-gray-500">sekund</span>
            {savedMsg && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 size={13} /> {savedMsg}
              </span>
            )}
          </div>
        </div>

        {/* Upload */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Upload size={16} className="text-red-600" />
            <h2 className="font-semibold text-gray-900 text-sm">Nahrát nové bannery</h2>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Podporované formáty: JPG, PNG. Max. {MAX_FILE_MB} MB na soubor. Doporučené rozměry: 800 × 170 px.
          </p>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png" multiple onChange={handleFiles} className="hidden" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {uploading ? 'Nahrávám...' : 'Vybrat soubory z počítače'}
          </button>
          {uploadError && (
            <p className="text-sm text-red-600 mt-3 flex items-start gap-1.5 bg-red-50 rounded-xl px-3 py-2">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {uploadError}
            </p>
          )}
        </div>

        {/* Image list */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ImageIcon size={16} className="text-red-600" />
              <h2 className="font-semibold text-gray-900 text-sm">
                Nahrané bannery
                <span className="ml-1.5 text-xs font-normal text-gray-400">({images.length})</span>
              </h2>
            </div>
            {loading && <Loader2 size={15} className="animate-spin text-gray-400" />}
          </div>

          {images.length === 0 && !loading ? (
            <div className="text-center py-12 text-gray-400">
              {loadError ? (
                <>
                  <AlertCircle size={28} className="mx-auto mb-2 text-red-400 opacity-70" />
                  <p className="text-sm text-red-500 mb-3">Server se právě probouzí, zkuste to za chvíli.</p>
                  <button onClick={loadBanners} className="text-xs text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50 transition-colors">Zkusit znovu</button>
                </>
              ) : (
                <>
                  <ImageIcon size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Zatím žádné bannery. Nahrajte první obrázek výše.</p>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {images.map((img, i) => (
                <div key={img.id} className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
                  {/* Banner náhled */}
                  <div className="relative group bg-gray-100" style={{ aspectRatio: '4.7/1', minHeight: '70px' }}>
                    <img src={`${API_BASE}${img.url}`} alt={img.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {i > 0 && (
                        <button onClick={() => moveImage(i, -1)} className="w-7 h-7 bg-white/20 hover:bg-white/40 rounded-lg flex items-center justify-center transition-colors text-white text-xs font-bold">←</button>
                      )}
                      <button onClick={() => handleDelete(img.id)} className="w-8 h-8 bg-red-600 hover:bg-red-700 rounded-lg flex items-center justify-center transition-colors" aria-label="Smazat banner">
                        <Trash2 size={14} className="text-white" />
                      </button>
                      {i < images.length - 1 && (
                        <button onClick={() => moveImage(i, 1)} className="w-7 h-7 bg-white/20 hover:bg-white/40 rounded-lg flex items-center justify-center transition-colors text-white text-xs font-bold">→</button>
                      )}
                    </div>
                    <div className="absolute top-1.5 left-1.5 bg-black/50 rounded-md px-1.5 py-0.5">
                      <span className="text-white text-xs font-medium">{i + 1}</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                      <p className="text-white text-xs truncate">{img.name}</p>
                    </div>
                  </div>
                  {/* Click akce */}
                  <div className="px-4 py-3 border-t border-gray-100">
                    <button
                      onClick={() => setExpandedClick(expandedClick === img.id ? null : img.id)}
                      className="flex items-center justify-between w-full text-xs text-gray-500 hover:text-gray-800 transition-colors"
                    >
                      <span className="flex items-center gap-1.5">
                        <MousePointerClick size={13} />
                        Akce při kliknutí:&nbsp;
                        <span className="font-medium text-gray-700">
                          {img.clickType === 'url' ? '🔗 Odkaz' : img.clickType === 'pdf' ? `📄 PDF${img.pdfName ? ` (${img.pdfName})` : ''}` : 'žádná'}
                        </span>
                      </span>
                      {expandedClick === img.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>

                    {expandedClick === img.id && (
                      <div className="mt-3 space-y-3">
                        {/* Typ akce */}
                        <div className="flex gap-2">
                          {[{ v: 'none', label: 'Žádná' }, { v: 'url', label: '🔗 URL odkaz' }, { v: 'pdf', label: '📄 PDF' }].map(opt => (
                            <button
                              key={opt.v}
                              onClick={() => updateImageField(img.id, 'clickType', opt.v)}
                              className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${(img.clickType || 'none') === opt.v ? 'bg-red-600 text-white border-red-600' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300'}`}
                            >{opt.label}</button>
                          ))}
                        </div>

                        {/* URL input */}
                        {img.clickType === 'url' && (
                          <div className="flex gap-2">
                            <input
                              type="url"
                              placeholder="https://..."
                              value={img.clickTarget || ''}
                              onChange={e => updateImageField(img.id, 'clickTarget', e.target.value)}
                              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                            <button
                              onClick={() => handleSaveClick(img)}
                              disabled={clickSaving[img.id]}
                              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium disabled:opacity-50 flex items-center gap-1"
                            >
                              {clickSaving[img.id] ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Uložit
                            </button>
                          </div>
                        )}

                        {/* PDF upload */}
                        {img.clickType === 'pdf' && (
                          <div className="space-y-2">
                            {img.pdfName && (
                              <p className="text-xs text-green-600 flex items-center gap-1"><FileText size={12} /> {img.pdfName}</p>
                            )}
                            <input
                              ref={el => pdfRefs.current[img.id] = el}
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              onChange={e => handlePdfUpload(img.id, e.target.files[0])}
                            />
                            <button
                              onClick={() => pdfRefs.current[img.id]?.click()}
                              disabled={clickSaving[img.id]}
                              className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                            >
                              {clickSaving[img.id] ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                              {img.pdfName ? 'Vyměnit PDF' : 'Nahrát PDF'}
                            </button>
                          </div>
                        )}

                        {/* Uložit "žádná" */}
                        {(!img.clickType || img.clickType === 'none') && (
                          <button
                            onClick={() => handleSaveClick(img)}
                            disabled={clickSaving[img.id]}
                            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
                          >
                            {clickSaving[img.id] ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Uložit
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 pb-4">
          Obrázky jsou uloženy na serveru a zobrazí se na všech zařízeních automaticky.
        </p>
      </div>
      )}

      {activeTab === 'analytika' && (
      <div className="max-w-5xl mx-auto p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Návštěvnost webu</h2>
          <button onClick={loadStats} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 transition-colors border border-gray-200 rounded-lg px-3 py-1.5">
            {statsLoading ? <Loader2 size={12} className="animate-spin" /> : '↻'} Obnovit
          </button>
        </div>

        {statsLoading && !stats ? (
          <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
        ) : stats ? (<>

          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Celkem návštěv', value: stats.total, icon: BarChart2, color: 'text-blue-600 bg-blue-50' },
              { label: 'Unikátní IP', value: stats.uniqueIPs, icon: Users, color: 'text-purple-600 bg-purple-50' },
              { label: 'Dnes', value: stats.todayCount, icon: MapPin, color: 'text-green-600 bg-green-50' },
              { label: 'Tento týden', value: stats.weekCount, icon: Globe, color: 'text-orange-600 bg-orange-50' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                  <s.icon size={17} />
                </div>
                <p className="text-2xl font-bold text-gray-900">{s.value ?? '—'}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Mapa */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2"><Globe size={15} className="text-red-600" /> Mapa lokalit</h3>
            <div style={{ height: 340 }} className="rounded-xl overflow-hidden">
              <MapContainer center={[48, 15]} zoom={4} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                {(stats.recent || []).filter(v => v.lat && v.lng).map((v, i) => (
                  <CircleMarker key={i} center={[v.lat, v.lng]} radius={6} pathOptions={{ color: '#dc2626', fillColor: '#dc2626', fillOpacity: 0.6 }}>
                    <Tooltip>{v.city || v.country || v.ip}<br /><span className="text-xs text-gray-400">{new Date(v.createdAt).toLocaleDateString('cs')}</span></Tooltip>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
          </div>

          {/* Top země */}
          {stats.countries?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2"><Globe size={15} className="text-red-600" /> Top země</h3>
              <div className="space-y-2">
                {stats.countries.map(c => (
                  <div key={c._id} className="flex items-center gap-3">
                    {c.countryCode && <img src={`https://flagcdn.com/20x15/${c.countryCode.toLowerCase()}.png`} alt={c._id} className="rounded-sm" />}
                    <span className="text-sm text-gray-700 flex-1">{c._id || 'Neznámá'}</span>
                    <span className="text-sm font-semibold text-gray-900">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Poslední návštěvy */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2"><Users size={15} className="text-red-600" /> Poslední návštěvy</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-100">
                    <th className="text-left py-2 pr-4 font-medium">IP adresa</th>
                    <th className="text-left py-2 pr-4 font-medium">Lokace</th>
                    <th className="text-left py-2 font-medium">Čas</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.recent || []).slice(0, 50).map((v, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 pr-4 font-mono text-gray-700">{v.ip || '—'}</td>
                      <td className="py-2 pr-4 text-gray-600">
                        {v.countryCode && <img src={`https://flagcdn.com/16x12/${v.countryCode.toLowerCase()}.png`} alt="" className="inline mr-1.5 rounded-sm" />}
                        {[v.city, v.country].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="py-2 text-gray-400">{new Date(v.createdAt).toLocaleString('cs', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>) : (
          <p className="text-center text-sm text-gray-400 py-12">Nepodařilo se načíst statistiky.</p>
        )}
      </div>
      )}
    </div>
  );
};

export default AdminPanel;
